/**
 * In-memory JWT access-token store for the admin panel (Token -> JWT
 * migration).
 *
 * Mirrors font-end's TokenStore (services/token-store.ts). The access token
 * is deliberately NOT in localStorage or sessionStorage - both are readable
 * by any script on the page, so one XSS anywhere in this app hands an
 * attacker a working credential. Holding it in a module-scoped variable
 * means it dies with the tab.
 *
 * The refresh token is never here at all - it lives in the HttpOnly
 * `sale4all_refresh` cookie the backend sets, which JavaScript cannot read.
 *
 * The legacy DRF token stays exactly where it already was (localStorage,
 * via ApiUrl/UsersService) and is untouched by this file. An admin who has
 * not signed in again since this migration keeps working on it until their
 * next login picks up a JWT pair - see ApiUrl.getJWTToken().
 */
export const LOGGED_IN_KEY = 'jwt_is_logged_in';

let accessToken: string | null = null;

export const TokenStore = {
  set(token: string | null): void {
    accessToken = token || null;
    try {
      if (accessToken) {
        localStorage.setItem(LOGGED_IN_KEY, '1');
      } else {
        localStorage.removeItem(LOGGED_IN_KEY);
      }
    } catch (e) {
      // Private mode / storage disabled - the in-memory token still works.
    }
  },

  get(): string | null {
    return accessToken;
  },

  hasToken(): boolean {
    return !!accessToken;
  },

  /** True when a JWT session is believed to exist - only a hint, the server
   * is the authority. Used by restoreSession() to decide whether a silent
   * refresh is worth attempting at all. */
  isLoggedIn(): boolean {
    if (accessToken) return true;
    try {
      return !!localStorage.getItem(LOGGED_IN_KEY);
    } catch (e) {
      return false;
    }
  },

  clear(): void {
    accessToken = null;
    try {
      localStorage.removeItem(LOGGED_IN_KEY);
    } catch (e) {
      // ignore
    }
  },
};
